'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import {
  useLearningSource,
  useBlockPart,
  tx,
  type LearningItem,
} from '@akademi/site-builder-runtime';
import {
  resolveCourseCard,
  type CourseCardVariant,
  type ButtonRadius,
  type CertificateDisplay,
  type PopoverPosition,
  type ProgressTextPlace,
} from '../cards/registry';
import { getGridSpec, type CardGridSpec } from '../cards/gridSpecs';
import { learningItemToCardProps } from '../cards/learningItemToCardProps';
import { SectionShell } from '../_shared/SectionShell';
import { type PaddingBox } from '../_shared/resolvePadding';
import { EmptyState } from '../_shared/EmptyState';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { Button, Input, SelectField } from '@akademi/ui';

/**
 * Searchable, filterable, paginated grid of course cards — the list-page
 * counterpart to the {@code CourseRow} rail. Where {@code CourseRow}
 * renders a fixed feed in a horizontal carousel, {@code CourseGrid} is
 * the catalog surface: a search bar, a sort dropdown, and a "show more"
 * button, all driven by the page URL.
 *
 * <h3>Catalog mode fetches the {@code /api/learning} feed</h3>
 * The default {@code source: 'courses'} ("all courses") reads through the
 * {@link LearningSource#fetchRow} port — the host's {@code GET
 * /api/learning?source=&limit=&offset=} feed endpoint. The sort dropdown
 * selects the feed token; the search term and the facet filters listed
 * below are parsed from the URL and sent along as {@code fetchRow}'s
 * {@code filter} argument, which the route forwards to the faceted legacy
 * {@code catalog/search} endpoint whenever any facet is active (see
 * {@code hasFilter} in {@code /api/learning}'s route handler) — otherwise
 * it stays on the faster sort-only feed.
 *
 * <h3>URL is the source of truth</h3>
 * The block reads its query from the URL search params and writes back to
 * them, so results are bookmarkable, survive refresh, and respond to the
 * browser back/forward buttons — the same pattern {@code CategoryTree}
 * uses for {@code ?category=}. Two {@code CourseGrid}s (or a category
 * sidebar + a grid) on one page stay in sync through the URL with no
 * shared React state.
 *
 * <table>
 *   <tr><td>{@code ?q=}</td><td>{@code filter.searchTerm}</td></tr>
 *   <tr><td>{@code ?sort=}</td><td>{@code sortField} + {@code sortOrder}</td></tr>
 *   <tr><td>{@code ?category=}</td><td>{@code filter.categoryIds} (csv)</td></tr>
 *   <tr><td>{@code ?level=}</td><td>{@code filter.courseLevels} (csv, upper-cased)</td></tr>
 *   <tr><td>{@code ?ct=}</td><td>{@code filter.courseTypes} (csv, upper-cased)</td></tr>
 *   <tr><td>{@code ?lng=}</td><td>{@code filter.localeIds} (csv)</td></tr>
 *   <tr><td>{@code ?skipRegistered=true}</td><td>{@code filter.skipRegistered}</td></tr>
 *   <tr><td>{@code ?demanded=true}</td><td>{@code filter.demanded}</td></tr>
 * </table>
 *
 * <p>Param names + id casing intentionally match the CategoryTree filter
 * sources (channel → {@code ct}, language → {@code lng}), whose ids are
 * lower-cased in the URL and upper-cased back here for the backend.
 *
 * <p>"Show more" grows {@code maxResults} (firstResult stays 0) and
 * refetches, so the visible set always matches the active query without
 * client-side page-merge bookkeeping. Already-loaded cards stay on screen
 * during the next fetch to avoid a layout flash.
 */

export type CourseGridCardStyle =
  | 'default'
  | 'compact'
  | 'wide'
  | 'minimal'
  | 'cinematic'
  | 'marketplace'
  | 'academic'
  | 'udemy'
  | 'classicHigh'
  | 'classicNetflixZoom'
  | 'borderless'
  | 'custom';

/** How many cards sit in a grid row. {@code 'auto'} keeps the responsive
 *  width-driven fit (as many as fit the card's min width); a number fixes
 *  the count, collapsing to 2 then 1 on narrower viewports. */
export type CourseGridColumns = 'auto' | '2' | '3' | '4' | '5' | '6';

/** A sort dropdown choice: the {@code value} is what lands in {@code ?sort=},
 *  {@code labelKey} is the i18n key (common.json) for the visible label,
 *  and {@code sortField} is the backend sort token — in catalog mode it is
 *  sent verbatim as the {@code /api/learning} feed {@code source}, which
 *  accepts every token below as an alias. {@code sortOrder} is
 *  optional and should stay unset unless a specific field is verified to
 *  need an explicit direction — the backend applies its own default
 *  direction per field otherwise (e.g. the reference LMS's real
 *  {@code {sortField: "popular"}} request carries no {@code sortOrder}
 *  at all). */
export interface CourseGridSortOption {
  value: string;
  labelKey: string;
  sortField: string;
  sortOrder?: 'ASCENDING' | 'DESCENDING';
}

export interface CourseGridProps {
  title?: string;
  cardStyle?: CourseGridCardStyle;
  /** Cards per grid row. {@code 'auto'} (default) fits as many as the card
   *  width allows; a fixed number renders exactly that many per row on
   *  desktop, collapsing to 2 then 1 on narrower screens. */
  columns?: CourseGridColumns;
  /** Show the course-card star rating. Defaults to shown. */
  showRating?: boolean;
  /** CTA button corner radius. Empty/default inherits the theme radius. */
  buttonRadius?: ButtonRadius;
  /** How the certificate marker renders on the udemy card's hover popover:
   *  badge image, text pill, or both. Defaults to both. */
  certificateDisplay?: CertificateDisplay;
  /** Autoplay the preview clip inside the udemy card's hover popover.
   *  Defaults to false (static poster + play badge). */
  autoPlayPreview?: boolean;
  /** Where the udemy card's hover popover opens: auto (default, picks
   *  left/right based on space), left, center (overlays the card), or right. */
  popoverPosition?: PopoverPosition;
  /** When true (default), the udemy card's hover-popover title links to
   *  the course, same as the card face. */
  popoverTitleLink?: boolean;
  /** Progress bar shown above the udemy card's footer for courses carrying
   *  user progress. {@code show} (default true) toggles the bar;
   *  {@code showText} (default false) adds the percentage label;
   *  {@code textPlace} positions that label beside or on top of the track. */
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
  /** Items fetched per page; "show more" adds another page. */
  pageSize?: number;
  /** Catalog document type sent in the filter. Default {@code 'course'}. */
  documentType?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  showSort?: boolean;
  /** Which {@code ?sort=} value is active when the URL has none. */
  defaultSort?: string;
  /** Restrict to "demanded" courses regardless of URL. When unset, falls
   *  back to the {@code ?demanded=true} URL param (written by the
   *  CategoryTree block's `demanded` feed source). */
  demanded?: boolean;
  showResultCount?: boolean;
  showMoreLabel?: string;
  emptyTitle?: string;
  /** Top padding of the section wrapper. {@code default} keeps the shared
   *  section rhythm (48px); {@code tight} trims it (16px); {@code none}
   *  removes it. Only affects this block — other sections are untouched. */
  topSpacing?: 'default' | 'tight' | 'none';
  /** href template for cards. Default {@code /courses/{slug}-{id}}. */
  courseHrefTemplate?: string;
  /**
   * Data source mode.
   * - {@code "courses"} (default) — the "all courses" feed, served by
   *   {@code GET /api/learning} via {@code LearningSource.fetchRow}. The
   *   sort dropdown picks the feed token; the search bar and URL filters
   *   ride along as {@code fetchRow}'s {@code filter} argument and narrow
   *   the results whenever the route can apply them.
   * - {@code "trainer_courses"} — fetches courses taught by the instructor
   *   whose ID is the last URL path segment (e.g. {@code /trainer/123}).
   *   Search/sort chrome is hidden.
   * - {@code "coordinator_courses"} — fetches courses coordinated by the user
   *   whose ID is the last URL path segment (e.g. {@code /coordinator/123}).
   *   Search/sort chrome is hidden.
   */
  source?: 'courses' | 'trainer_courses' | 'coordinator_courses';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
  titleFontSize?: string;
}

const DEFAULT_PAGE_SIZE = 12;
const MIN_PAGE_SIZE = 1;
const MAX_PAGE_SIZE = 48;
const SEARCH_DEBOUNCE_MS = 350;

/** Default sort menu. Labels resolve from the portal's common.json at
 *  render time via i18next; {@code sortField} values map to the backend
 *  {@code catalog/search} sort contract, verbatim tokens from the
 *  reference LMS's own sort fields. No option sets {@code sortOrder} —
 *  none of these are confirmed to need an explicit direction, so the
 *  backend's own per-field default applies, matching the reference LMS's
 *  real request shape. */
const DEFAULT_SORT_OPTIONS: CourseGridSortOption[] = [
  { value: 'recent_desc', labelKey: 'label.latest', sortField: 'recent' },
  { value: 'popular', labelKey: 'label.popularity', sortField: 'popular' },
  { value: 'comment', labelKey: 'title.mostCommented', sortField: 'comment' },
  { value: 'viewed', labelKey: 'label.mostViewed', sortField: 'viewed' },
  { value: 'liked', labelKey: 'label.mostLiked', sortField: 'liked' },
  { value: 'title', labelKey: 'label.nameAll', sortField: 'title' },
];

/** {@link CourseGridProps.topSpacing} → the {@code paddingOuter.top}
 *  space-token that produces it (see {@code resolvePadding.ts}'s
 *  {@code SPACE_FALLBACKS}: token '12' → 48px, '4' → 16px, '0' → 0px). */
const TOP_SPACING_TOKEN: Record<NonNullable<CourseGridProps['topSpacing']>, string> = {
  default: '12',
  tight: '4',
  none: '0',
};

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function resolvePageSize(raw: number | undefined): number {
  const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : DEFAULT_PAGE_SIZE;
  return clamp(Math.trunc(n), MIN_PAGE_SIZE, MAX_PAGE_SIZE);
}

/** Split a csv search param into a trimmed, non-empty string list. */
function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Same as {@link parseCsv} but keeps only finite integers (for id lists). */
function parseIdCsv(raw: string | null): number[] {
  return parseCsv(raw)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));
}

export function CourseGrid({
  title,
  cardStyle = 'default',
  columns = '5',
  showRating = true,
  buttonRadius,
  certificateDisplay,
  autoPlayPreview,
  popoverPosition,
  popoverTitleLink,
  progressBar,
  pageSize: rawPageSize,
  documentType = 'course',
  showSearch = true,
  searchPlaceholder,
  showSort = true,
  defaultSort,
  demanded,
  showResultCount = true,
  showMoreLabel,
  emptyTitle,
  topSpacing = 'tight',
  courseHrefTemplate,
  source = 'courses',
  paddingOuter,
  paddingInner,
  titleFontSize = '3xl',
}: CourseGridProps) {
  const learning = useLearningSource();
  const part = useBlockPart();
  const { t } = useTranslation('common');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams();

  // For profile-scoped sources, read user ID from ?id= query param.
  const profileUserId = useMemo(() => {
    if (source === 'courses') return undefined;
    const id = params?.id ?? '';
    return /^\d+$/.test(String(id)) ? Number(id) : undefined;
  }, [source, params]);

  // Localized chrome strings. Operator-authored props win when set
  // (coerced via tx for localized bundles); otherwise fall back to the
  // shared portal common.json keys.
  const searchPlaceholderText = searchPlaceholder
    ? tx(searchPlaceholder)
    : t('placeholder.searchCourse');
  const showMoreText = showMoreLabel ? tx(showMoreLabel) : t('button.showMore');
  const emptyTitleText = emptyTitle ? tx(emptyTitle) : t('title.noResulFound');

  const pageSize = resolvePageSize(rawPageSize);
  const sortOptions = DEFAULT_SORT_OPTIONS;

  // `topSpacing` only ever set the destructured default — nothing read it
  // afterwards, so every instance rendered with SectionShell's ordinary
  // 48px top rhythm regardless of the prop. An explicit `paddingOuter.top`
  // (the advanced/full padding control) still wins over it.
  const effectivePaddingOuter: PaddingBox = {
    ...paddingOuter,
    top: paddingOuter?.top ?? TOP_SPACING_TOKEN[topSpacing],
  };

  // --- Read query from the URL (single source of truth) -----------------
  const searchTerm = (searchParams?.get('q') ?? '').trim();
  const sortValue = searchParams?.get('sort') ?? defaultSort ?? sortOptions[0]?.value ?? '';
  const activeSort = sortOptions.find((o) => o.value === sortValue) ?? sortOptions[0];

  // Param names + value casing match what the CategoryTree filter writes:
  // channel → ?ct=, language → ?lng=, level → ?level= (ids are lowercased in
  // the URL for readability; the backend filter wants them upper-cased back,
  // e.g. ?ct=f2f_course → courseTypes: ["F2F_COURSE"]).
  const categoryIds = parseIdCsv(searchParams?.get('category') ?? null);
  const courseLevels = parseCsv(searchParams?.get('level') ?? null).map((v) => v.toUpperCase());
  const courseTypes = parseCsv(searchParams?.get('ct') ?? null).map((v) => v.toUpperCase());
  const localeIds = parseIdCsv(searchParams?.get('lng') ?? null);
  const skipRegistered = searchParams?.get('skipRegistered') === 'true';
  const demandedParam = searchParams?.get('demanded') === 'true';

  // Stable dep keys so the fetch effect only re-runs when the query changes.
  const categoryKey = categoryIds.join(',');
  const levelKey = courseLevels.join(',');
  const typeKey = courseTypes.join(',');
  const localeKey = localeIds.join(',');
  const catalogQueryKey = `${searchTerm}|${sortValue}|${categoryKey}|${levelKey}|${typeKey}|${localeKey}|${documentType}|${demanded}|${demandedParam}|${skipRegistered}`;

  // --- Pagination (show more) -------------------------------------------
  // Each "show more" click fetches only the next page (firstResult
  // advances, maxResults stays pageSize) and appends it, instead of
  // re-asking for an ever-larger maxResults from firstResult 0. The BFF's
  // catalog/search proxy clamps maxResults per request (MAX_PAGE in
  // search-courses/route.ts) — a growing single-request maxResults would
  // hit that ceiling after a few clicks and silently cap the whole list.
  const [pageCount, setPageCount] = useState(1);
  useEffect(() => {
    setPageCount(1);
  }, [source, profileUserId, catalogQueryKey]);

  // --- Fetch ------------------------------------------------------------
  const [items, setItems] = useState<LearningItem[]>([]);
  // `null` until the first fetch for the current query resolves.
  const [total, setTotal] = useState<number | null>(null);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  // Item count the most recent single-page fetch returned. If it came back
  // shorter than `pageSize`, the feed is exhausted — a reliable "no more
  // pages" signal even when `total` is reported too high to ever be reached.
  const [lastPageCount, setLastPageCount] = useState(0);

  const isProfileSource = source === 'trainer_courses' || source === 'coordinator_courses';
  const hasFetchRow = typeof learning?.fetchRow === 'function';

  useEffect(() => {
    const ctrl = new AbortController();
    const firstResult = (pageCount - 1) * pageSize;
    const isFreshPage = pageCount === 1;
    setStatus('loading');

    if (isProfileSource) {
      // Profile-scoped mode: fetch courses for a specific trainer or coordinator.
      if (!profileUserId) {
        setItems([]);
        setTotal(0);
        setLastPageCount(0);
        setStatus('loaded');
        return;
      }
      const type = source === 'trainer_courses' ? 'trainer' : 'coordinator';
      fetch(`${typeof window !== 'undefined' ? '' : ''}/api/profile-courses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: profileUserId,
          type,
          firstResult,
          maxResults: pageSize,
          sortField: activeSort?.sortField ?? 'recent',
          sortOrder: activeSort?.sortOrder ?? 'DESCENDING',
        }),
        signal: ctrl.signal,
        credentials: 'same-origin',
      })
        .then((r) => (r.ok ? r.json() : null))
        .then(
          (
            data: {
              totalRecords?: number;
              documents?: {
                id?: unknown;
                sourceId?: unknown;
                title?: unknown;
                thumbnail?: string;
                image?: string;
                description?: string;
                trainer?: { name?: string; surname?: string };
                learningLevel?: string;
                attendeeCount?: number;
                duration?: number;
                hasCertificate?: boolean;
              }[];
            } | null,
          ) => {
            if (ctrl.signal.aborted) return;
            const docs = Array.isArray(data?.documents) ? data.documents : [];

            const mapLevel = (level?: string) => {
              switch ((level ?? '').toUpperCase()) {
                case 'BEGINNER':
                  return 'Beginner';
                case 'INTERMEDIATE':
                  return 'Intermediate';
                case 'ADVANCED':
                  return 'Advanced';
                default:
                  return undefined;
              }
            };
            const formatDuration = (seconds?: number) => {
              if (typeof seconds !== 'number' || seconds <= 0) return undefined;
              const h = Math.floor(seconds / 3600);
              const m = Math.round((seconds % 3600) / 60);
              if (h > 0) return m > 0 ? `${h} sa ${m} dk` : `${h} sa`;
              return `${m} dk`;
            };

            const mapped: LearningItem[] = docs.map((d) => {
              const trainer = d.trainer
                ? `${d.trainer.name ?? ''} ${d.trainer.surname ?? ''}`.trim()
                : undefined;
              return {
                id: String(d?.id ?? d?.sourceId ?? ''),
                title: String(d?.title ?? ''),
                imageUrl: d.thumbnail ?? d.image,
                description: d.description,
                instructor: trainer || undefined,
                level: mapLevel(d.learningLevel),
                enrolled: typeof d.attendeeCount === 'number' ? d.attendeeCount : undefined,
                duration: formatDuration(d.duration),
                hasCertificate: d.hasCertificate,
                kind: 'course' as const,
              };
            });

            setItems((prev) => (isFreshPage ? mapped : [...prev, ...mapped]));
            setTotal(typeof data?.totalRecords === 'number' ? data.totalRecords : mapped.length);
            setLastPageCount(mapped.length);
            setStatus('loaded');
          },
        )
        .catch((err: unknown) => {
          if (ctrl.signal.aborted) return;
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setStatus('error');
        });
    } else {
      // Catalog mode (default): the "all courses" feed, served by
      // GET /api/learning?source=&limit=&offset= through the fetchRow port.
      // The sort dropdown's `sortField` IS the feed `source` token (the
      // route accepts every DEFAULT_SORT_OPTIONS token as an alias). The
      // URL-driven search term / category / level / type / language facets
      // and the demanded/skipRegistered toggles ride along as `filter` —
      // the route falls back to the faceted legacy catalog/search endpoint
      // whenever any of them is set (see /api/learning's `hasFilter`).
      if (!learning?.fetchRow) return;
      learning
        .fetchRow({
          source: activeSort?.sortField ?? 'recent',
          limit: pageSize,
          offset: firstResult,
          filter: {
            documentType,
            ...(searchTerm ? { searchTerm } : {}),
            ...(categoryIds.length ? { categoryIds } : {}),
            ...(courseLevels.length ? { courseLevels } : {}),
            ...(courseTypes.length ? { courseTypes } : {}),
            ...(localeIds.length ? { localeIds } : {}),
            ...(typeof demanded === 'boolean'
              ? { demanded }
              : demandedParam
                ? { demanded: true }
                : {}),
            ...(skipRegistered ? { skipRegistered: true } : {}),
          },
          signal: ctrl.signal,
        })
        .then((row) => {
          if (ctrl.signal.aborted) return;
          // A miss returns null — render it as an empty (not errored) feed,
          // same as the row blocks do.
          const rowItems = row?.items ?? [];
          setItems((prev) => (isFreshPage ? rowItems : [...prev, ...rowItems]));
          setTotal(typeof row?.total === 'number' ? row.total : rowItems.length);
          setLastPageCount(rowItems.length);
          setStatus('loaded');
        })
        .catch((err: unknown) => {
          if (ctrl.signal.aborted) return;
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setStatus('error');
        });
    }

    return () => ctrl.abort();
  }, [learning, source, profileUserId, isProfileSource, catalogQueryKey, pageSize, pageCount]);

  // --- Debounced search input ------------------------------------------
  const [searchInput, setSearchInput] = useState(searchTerm);
  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const updateParams = useCallback(
    (patch: Record<string, string | undefined>) => {
      const params = new URLSearchParams(searchParams?.toString() ?? '');
      for (const [key, value] of Object.entries(patch)) {
        if (value == null || value === '') params.delete(key);
        else params.set(key, value);
      }
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [router, pathname, searchParams],
  );

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParams({ q: value.trim() || undefined });
    }, SEARCH_DEBOUNCE_MS);
  };
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const Card = useMemo(() => resolveCourseCard(cardStyle as CourseCardVariant), [cardStyle]);
  const gridSpec = getGridSpec(cardStyle);

  // The last page fetch came back full-sized, so more may exist; combined
  // with the reported total still being ahead of what we hold. If it came
  // back shorter than pageSize, the feed is exhausted and "show more" is
  // hidden regardless of `total`.
  const servedFullPage = lastPageCount >= pageSize;
  const hasMore = total !== null && servedFullPage && items.length < total;
  const isInitialLoad = status === 'loading' && total === null;
  const isLoadingMore = status === 'loading' && total !== null;

  // --- Adapter not wired ------------------------------------------------
  if (!isProfileSource && (!learning || !hasFetchRow)) {
    return (
      <SectionShell
        title={title}
        paddingOuter={effectivePaddingOuter}
        paddingInner={paddingInner}
        titleFontSize={titleFontSize}
      >
        <AdapterPlaceholder
          blockType="CourseGrid"
          adapterName="LearningSource"
          reason="Backend pending — fetchRow() not implemented"
        />
      </SectionShell>
    );
  }

  // Profile-scoped sources fetch directly — no URL-driven search/sort chrome.
  // Before the first load resolves, render skeleton placeholders in the
  // same slots instead of the real controls (or nothing) — otherwise the
  // search/sort bar either flashes in ahead of the cards or pops in after,
  // shifting the page either way.
  const toolbar =
    !isProfileSource && (showSearch || showSort) ? (
      <div {...part('filter')} style={toolbarStyle}>
        {showSearch &&
          (isInitialLoad ? (
            <div
              className="theme-skeleton"
              style={{
                flex: '0 1 320px',
                minWidth: 180,
                maxWidth: 320,
                height: 40,
                borderRadius: 8,
              }}
            />
          ) : (
            <div style={{ flex: '0 1 320px', minWidth: 180, maxWidth: 320 }}>
              <Input
                {...part('search')}
                type="search"
                value={searchInput}
                placeholder={searchPlaceholderText}
                onChange={(e) => onSearchChange(e.target.value)}
                aria-label={searchPlaceholderText}
              />
            </div>
          ))}
        {showSort && sortOptions.length > 0 && (
          <>
            {isInitialLoad ? (
              <div className="theme-skeleton" style={{ width: 140, height: 40, borderRadius: 8 }} />
            ) : (
              <SelectField
                value={activeSort?.value}
                onChange={(v) => updateParams({ sort: v })}
                options={sortOptions.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
                ariaLabel={t('label.sortBy')}
                placement="bottom-end"
              />
            )}
          </>
        )}
      </div>
    ) : null;

  // Keep showing the last-known total while a refetch (search/sort/filter
  // change) is in flight — `result` only clears on the very first load, so
  // gating on `status === 'loaded'` alone made this line disappear and
  // reappear on every refetch, shifting the page. Before the first load
  // resolves, show a skeleton line in the same spot instead of nothing.
  const resultCount =
    !isProfileSource && showResultCount && status !== 'error' ? (
      isInitialLoad ? (
        <div className="theme-skeleton" style={{ ...countStyle, width: 140, height: 14 }} />
      ) : (
        <div style={countStyle}>
          {total ?? 0} {t('label.learningFound')}
        </div>
      )
    ) : null;

  let body: React.ReactNode;
  if (isInitialLoad) {
    body = <SkeletonRow count={pageSize} layout="grid" minCardWidth={gridMinWidth(gridSpec)} />;
  } else if (status === 'error') {
    body = (
      <EmptyState
        title={t('message.errorOccuredDuringProcess')}
        hint={t('title.serviceUnavailableMessage')}
      />
    );
  } else if (items.length === 0) {
    body = <EmptyState title={emptyTitleText} hint={t('message.notFoundCriteria')} />;
  } else {
    body = (
      <>
        <CardGrid spec={gridSpec} columns={columns}>
          {items.map((c) => (
            <Card
              key={c.id}
              showRating={showRating}
              buttonRadius={buttonRadius}
              certificateDisplay={certificateDisplay}
              autoPlayPreview={autoPlayPreview}
              popoverPosition={popoverPosition}
              popoverTitleLink={popoverTitleLink}
              progressBar={progressBar}
              course={learningItemToCardProps(
                c,
                courseHrefTemplate ? { hrefTemplate: courseHrefTemplate } : {},
              )}
            />
          ))}
        </CardGrid>
        {isLoadingMore && (
          <div style={{ marginTop: 16 }}>
            <SkeletonRow count={pageSize} layout="grid" minCardWidth={gridMinWidth(gridSpec)} />
          </div>
        )}
        {hasMore && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
            <Button
              {...part('pager')}
              variant="outline"
              size="lg"
              disabled={isLoadingMore}
              onClick={() => setPageCount((c) => c + 1)}
            >
              {isLoadingMore ? `${t('label.searcing')}` : showMoreText}
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <SectionShell
      title={title}
      paddingOuter={effectivePaddingOuter}
      paddingInner={paddingInner}
      titleFontSize={titleFontSize}
      maxWidth="full"
    >
      {resultCount}
      {toolbar}
      {body}
    </SectionShell>
  );
}

/** Grid/list container — mirrors {@code CourseRow}'s layout switch but
 *  defaults a rail spec to a grid (a list page shouldn't scroll sideways). */
function CardGrid({
  spec,
  columns = 'auto',
  children,
}: {
  spec: CardGridSpec;
  columns?: CourseGridColumns;
  children: React.ReactNode;
}) {
  const part = useBlockPart();
  if (spec.kind === 'list') {
    const arr = Array.isArray(children) ? children : [children];
    return (
      <ul
        {...part('list')}
        style={{
          margin: 0,
          padding: 0,
          listStyle: 'none',
          borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
        }}
      >
        {arr.map((child, i) => (
          <li key={i} {...part('item', i)}>
            {child}
          </li>
        ))}
      </ul>
    );
  }

  const gap =
    spec.kind === 'grid' ? (spec.gap ?? 'clamp(12px, 2vw, 16px)') : 'clamp(12px, 2vw, 16px)';

  // Admin picked a fixed count: render exactly that many per row on desktop,
  // collapsing to 2 (≤900px) then 1 (≤560px) via a static scoped rule so
  // narrow viewports never crush the cards. The count differs per instance,
  // so it lives inline; only the uniform collapse lives in the shared class.
  const fixedCols = columns !== 'auto' ? Number(columns) : 0;
  // Both paths add a canceled horizontal buffer (padding + matching
  // negative margin, same trick ContentRail/CourseRow use): a card whose
  // hover effect grows past its own grid cell (lift/shadow, or a
  // Netflix-style zoom scale) has nowhere to bleed into at the first/last
  // column when the block's own paddingOuter is 0, since something
  // upstream generally clips horizontal overflow. The negative margin
  // cancels the padding back out, so this still lines up with
  // paddingOuter exactly at rest.
  const hoverBuffer = 'clamp(1.5rem, 4vw, 3rem)';

  if (fixedCols > 0) {
    return (
      <>
        <div
          {...part('list')}
          className="sb-course-grid--fixed"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${fixedCols}, minmax(0, 1fr))`,
            gap,
            paddingInline: hoverBuffer,
            marginInline: `calc(-1 * ${hoverBuffer})`,
          }}
        >
          {children}
        </div>
        <style dangerouslySetInnerHTML={{ __html: FIXED_GRID_RESPONSIVE_CSS }} />
      </>
    );
  }

  const minWidth = gridMinWidth(spec);
  return (
    <div
      {...part('list')}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(min(${minWidth}px, 100%), 1fr))`,
        gap,
        paddingInline: hoverBuffer,
        marginInline: `calc(-1 * ${hoverBuffer})`,
      }}
    >
      {children}
    </div>
  );
}

/** Collapse a fixed-column course grid on narrow viewports. Shared across
 *  instances — the per-instance column count is set inline, this only
 *  overrides it uniformly at the breakpoints. */
const FIXED_GRID_RESPONSIVE_CSS = `
@media (max-width: 1250px) {
    .sb-course-grid--fixed { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
  }  
@media (max-width: 900px) {
    .sb-course-grid--fixed { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  }
  @media (max-width: 560px) {
    .sb-course-grid--fixed { grid-template-columns: 1fr !important; }
  }
`;

function gridMinWidth(spec: CardGridSpec): number {
  return spec.kind === 'grid' ? spec.minCardWidth : 220;
}

const toolbarStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 'var(--space-6, 24px)',
};

const countStyle: CSSProperties = {
  // Sits directly under the section title: pull up to tighten the gap left
  // by SectionShell's title-row margin, then space before the toolbar.
  marginTop: -12,
  marginBottom: 16,
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-tertiary, #6a6f73)',
  whiteSpace: 'nowrap',
};
